'use client';

import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { normalizeAdObjectId } from '@terra/shared-contracts-internal-network';
import type {
  DomainDto,
  ExternalNetworkEntityKind,
  GatewayDetailDto,
  HostDto,
  NetBlockDto,
  NetworkServiceDto,
  SoftwareDto,
  StorageServiceDto,
} from '@terra/shared-contracts-external-network';
import type { InternalNetworkAdEntityKind } from '@terra/ui-graphs/internal-network';
import { ENTITY_KIND_COLOR_MAP as INTERNAL_ENTITY_KIND_COLOR_MAP } from '@terra/ui-graphs/internal-network';
import type { SurfaceAssetMainContentTab } from '../../store/layoutStore';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import {
  Badge,
  Button,
  DrawerGeneralDetails,
  Scroll,
  Skeleton,
  Tabs,
  Text,
  CopyIdButton,
} from '@terra/ui-design-system';
import { CrownIcon, Key01Icon, Link02Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { EmptyState } from '../EmptyState';
import { TARGET_TYPE } from '../../utils/target-scope.utils';
import { useTargetId } from '../../store/projectStore';
import {
  useExternalNetworkAssetDetail,
  useInternalNetworkAdEntity,
  useInternalNetworkAdEntityRelationships,
} from '../../hooks/api/useSurfaceAssetDetail';
import { terraProxyApiClient } from '../../clients/testerClient';
import { INTERNAL_NETWORK_GRAPH_KEY } from '../../hooks/api/useInternalNetworkGraph';
import { INTERNAL_NETWORK_STATS_KEY } from '../../hooks/api/useInternalNetworkStats';
import { SURFACE_ASSETS_KEY } from '../../hooks/api/useSurfaceAssets';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import { SignalsContent } from './SignalsContent';
import {
  EXTERNAL_NETWORK_ENTITY_KIND_COLOR_MAP,
  getExternalNetworkEntityKindIcon,
  getExternalNetworkEntityKindLabel,
  getInternalNetworkEntityKindLabel,
} from './surface-assets/surfaceAsset.constants';
import {
  getExternalAssetHeaderSubtitle,
  getExternalAssetHeaderTitle,
} from './surface-assets/externalAssetDisplayLabels';
import {
  buildExternalOverviewContent,
  buildInternalOverviewRows,
  RelationshipsSection,
} from './surface-assets/surfaceAssetDetailSections';
import { GRAPH_NODE_ICON } from '@terra/ui-graphs/internal-network';
import { CanonicalInternalNetworkAssetDetail } from './surface-assets/CanonicalInternalNetworkAssetDetail';
import { ExternalNetworkAssetRouteGraph } from './surface-assets/ExternalNetworkAssetRouteGraph';

type SurfaceAssetDetailTabProps = {
  tab: SurfaceAssetMainContentTab;
};
type ExternalAsset =
  | DomainDto
  | HostDto
  | GatewayDetailDto
  | NetworkServiceDto
  | NetBlockDto
  | SoftwareDto
  | StorageServiceDto;

function isExternalAsset(asset: unknown): asset is ExternalAsset {
  if (typeof asset !== 'object' || asset === null) {
    return false;
  }

  return (
    typeof (
      asset as {
        id?: unknown;
      }
    ).id === 'string'
  );
}

function SurfaceAssetHeaderIcon({
  isInternal,
  entityKind,
}: {
  isInternal: boolean;
  entityKind: InternalNetworkAdEntityKind | ExternalNetworkEntityKind;
}) {
  const Icon = isInternal
    ? GRAPH_NODE_ICON[entityKind as InternalNetworkAdEntityKind]
    : getExternalNetworkEntityKindIcon(entityKind as ExternalNetworkEntityKind);

  return (
    <Box display="flex" alignItems="center" flexShrink={0} aria-hidden>
      <Icon size={20} />
    </Box>
  );
}

export function SurfaceAssetDetailTab({ tab }: SurfaceAssetDetailTabProps) {
  if (
    tab.data.targetType === TARGET_TYPE.INTERNAL_NETWORK &&
    tab.data.surfaceAssetKind === 'ASSET'
  ) {
    return <CanonicalInternalNetworkAssetDetail tab={tab} />;
  }

  return <LegacySurfaceAssetDetailTab tab={tab} />;
}

function LegacySurfaceAssetDetailTab({ tab }: SurfaceAssetDetailTabProps) {
  const surfaceId = useTargetId();
  const tAssetDetail = useTranslations('externalNetwork.assetDetail');
  const tExternalEntityKinds = useTranslations('externalNetwork.entityKinds');
  const tInternalEntityKinds = useTranslations('internalNetwork.entityKinds');
  const tInternalNetwork = useTranslations('internalNetwork');
  const tAssetEntity = useTranslations('designSystem.signals.drawer');
  const queryClient = useQueryClient();
  const dateFormatter = useDateFormatter();
  const [selectedTab, setSelectedTab] = useState('overview');
  const { surfaceAssetKind, entityId, targetType } = tab.data;
  const isInternal = targetType === TARGET_TYPE.INTERNAL_NETWORK;
  const internalEntityKind = isInternal
    ? (surfaceAssetKind as InternalNetworkAdEntityKind)
    : null;
  const externalEntityKind = !isInternal
    ? (surfaceAssetKind as ExternalNetworkEntityKind)
    : null;
  const internalDetail = useInternalNetworkAdEntity({
    networkId: surfaceId,
    nodeId: isInternal ? entityId : null,
  });
  const internalRelationships = useInternalNetworkAdEntityRelationships({
    networkId: surfaceId,
    nodeId: isInternal ? entityId : null,
  });
  const externalDetail = useExternalNetworkAssetDetail({
    surfaceId,
    entityKind: externalEntityKind,
    entityId: isInternal ? null : entityId,
  });
  const entity = internalDetail.entity;
  const externalAsset = isExternalAsset(externalDetail.asset)
    ? externalDetail.asset
    : null;
  const entityKindLabel = isInternal
    ? getInternalNetworkEntityKindLabel({
        kind: internalEntityKind ?? 'USER',
        t: tInternalEntityKinds,
      })
    : getExternalNetworkEntityKindLabel({
        kind: externalEntityKind ?? 'HOST',
        t: tExternalEntityKinds,
      });
  const entityKindColorPalette = isInternal
    ? INTERNAL_ENTITY_KIND_COLOR_MAP[internalEntityKind ?? 'USER']
    : EXTERNAL_NETWORK_ENTITY_KIND_COLOR_MAP[externalEntityKind ?? 'HOST'];
  const headerTitle = isInternal
    ? (entity?.displayName ?? tab.title)
    : externalAsset
      ? getExternalAssetHeaderTitle({
          entityKind: externalEntityKind ?? 'HOST',
          asset: externalAsset,
          fallbackTitle: tab.title,
        })
      : tab.title;
  const headerSubtitle = isInternal
    ? (entity?.distinguishedName ?? entity?.objectId ?? entityId)
    : getExternalAssetHeaderSubtitle({
        entityKind: externalEntityKind ?? 'HOST',
        asset: externalAsset,
      });
  const crownJewelsQuery = useQuery({
    queryKey: [SURFACE_ASSETS_KEY, 'crown-jewels', surfaceId],
    queryFn: () =>
      terraProxyApiClient.listInternalNetworkCrownJewels(surfaceId ?? ''),
    enabled: Boolean(surfaceId) && isInternal && Boolean(entity?.isCrownJewel),
  });
  const matchingCrownJewel = useMemo(() => {
    if (!entity?.objectId || !entity.entityKind) {
      return null;
    }

    return (
      crownJewelsQuery.data?.data.find(
        (jewel) =>
          jewel.entityKind === entity.entityKind &&
          normalizeAdObjectId(jewel.entityId) ===
            normalizeAdObjectId(entity.objectId),
      ) ?? null
    );
  }, [crownJewelsQuery.data?.data, entity?.entityKind, entity?.objectId]);
  const invalidateInternalQueries = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: [SURFACE_ASSETS_KEY, 'internal-detail', surfaceId, entityId],
      }),
      queryClient.invalidateQueries({
        queryKey: [INTERNAL_NETWORK_STATS_KEY, surfaceId],
      }),
      queryClient.invalidateQueries({
        queryKey: [INTERNAL_NETWORK_GRAPH_KEY, surfaceId],
      }),
      queryClient.invalidateQueries({
        queryKey: [SURFACE_ASSETS_KEY, 'internal', surfaceId],
      }),
    ]);
  }, [entityId, queryClient, surfaceId]);
  const createCrownJewelMutation = useMutation({
    mutationFn: () => {
      if (!entity?.objectId || !entity.entityKind) {
        throw new Error('Entity is missing required fields');
      }

      return terraProxyApiClient.createInternalNetworkCrownJewel({
        networkId: surfaceId ?? '',
        body: {
          entityKind: entity.entityKind,
          entityId: entity.objectId,
          label: entity.displayName,
        },
      });
    },
    onSuccess: invalidateInternalQueries,
  });
  const createOwnedPrincipalMutation = useMutation({
    mutationFn: () => {
      if (!entity?.objectId || !entity.entityKind) {
        throw new Error('Entity is missing required fields');
      }

      return terraProxyApiClient.createInternalNetworkOwnedPrincipal({
        networkId: surfaceId ?? '',
        body: {
          entityKind: entity.entityKind,
          entityId: entity.objectId,
          label: entity.displayName,
          source: 'MANUAL',
        },
      });
    },
    onSuccess: invalidateInternalQueries,
  });
  const deleteCrownJewelMutation = useMutation({
    mutationFn: (crownJewelId: string) =>
      terraProxyApiClient.deleteInternalNetworkCrownJewel({
        networkId: surfaceId ?? '',
        crownJewelId,
      }),
    onSuccess: invalidateInternalQueries,
  });
  const revokeOwnedPrincipalMutation = useMutation({
    mutationFn: (ownedEntityId: string) =>
      terraProxyApiClient.revokeInternalNetworkOwnedPrincipal({
        networkId: surfaceId ?? '',
        entityId: ownedEntityId,
      }),
    onSuccess: invalidateInternalQueries,
  });
  const isLoading = isInternal
    ? internalDetail.isLoading
    : externalDetail.isLoading;
  const internalOverviewRows = useMemo(() => {
    if (!entity || !internalEntityKind) {
      return [];
    }

    const crownControl = entity.isCrownJewel ? (
      <Button
        size="xs"
        variant="outline"
        loading={deleteCrownJewelMutation.isPending}
        disabled={!matchingCrownJewel}
        onClick={() => {
          if (matchingCrownJewel) {
            deleteCrownJewelMutation.mutate(matchingCrownJewel.id);
          }
        }}
      >
        <CrownIcon size={14} />
        Remove
      </Button>
    ) : (
      <Button
        size="xs"
        variant="outline"
        loading={createCrownJewelMutation.isPending}
        onClick={() => createCrownJewelMutation.mutate()}
      >
        <CrownIcon size={14} />
        Mark
      </Button>
    );
    const ownedControl = entity.isOwned ? (
      <Button
        size="xs"
        variant="outline"
        loading={revokeOwnedPrincipalMutation.isPending}
        onClick={() => revokeOwnedPrincipalMutation.mutate(entity.objectId)}
      >
        <Key01Icon size={14} />
        Remove
      </Button>
    ) : (
      <Button
        size="xs"
        variant="outline"
        loading={createOwnedPrincipalMutation.isPending}
        onClick={() => createOwnedPrincipalMutation.mutate()}
      >
        <Key01Icon size={14} />
        Mark
      </Button>
    );

    return buildInternalOverviewRows({
      entity,
      entityKind: internalEntityKind,
      crownJewelControl: crownControl,
      ownedPrincipalControl: ownedControl,
      t: tAssetDetail,
      tEntityKinds: tInternalEntityKinds,
    });
  }, [
    createCrownJewelMutation,
    createOwnedPrincipalMutation,
    deleteCrownJewelMutation,
    entity,
    internalEntityKind,
    matchingCrownJewel,
    revokeOwnedPrincipalMutation,
    tAssetDetail,
    tInternalEntityKinds,
  ]);

  if (isLoading) {
    return (
      <Box
        h="full"
        w="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack gap="4">
          <Text fontSize="lg" color="fg.muted">
            Loading asset data...
          </Text>
        </VStack>
      </Box>
    );
  }

  if ((isInternal && !entity) || (!isInternal && !externalAsset)) {
    return (
      <Box
        h="full"
        w="full"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <EmptyState
          icon={
            isInternal
              ? GRAPH_NODE_ICON[internalEntityKind ?? 'USER']
              : getExternalNetworkEntityKindIcon(externalEntityKind ?? 'HOST')
          }
          title={tAssetDetail('notFoundTitle')}
          description={tAssetDetail('notFoundDescription')}
        />
      </Box>
    );
  }

  return (
    <Box h="full" w="full" overflow="hidden">
      <VStack gap="0" h="full" alignItems="stretch">
        <Box p="4" borderBottomWidth="1px" borderColor="border.subtle">
          <VStack gap="2" alignItems="flex-start" w="full">
            <HStack gap="3" alignItems="center" w="full">
              <SurfaceAssetHeaderIcon
                isInternal={isInternal}
                entityKind={
                  isInternal
                    ? (internalEntityKind ?? 'USER')
                    : (externalEntityKind ?? 'HOST')
                }
              />
              <Badge
                variant="outline"
                size="sm"
                colorPalette={entityKindColorPalette}
              >
                {entityKindLabel}
              </Badge>
              <Text
                fontSize="md"
                fontWeight="semibold"
                color="fg.default"
                flex="1"
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
              >
                {headerTitle}
              </Text>
              <CopyIdButton id={entityId} entityName={tAssetEntity('asset')} />
              {isInternal && entity?.isCrownJewel ? (
                <Badge variant="outline" size="sm">
                  {tInternalNetwork('crownJewels.title')}
                </Badge>
              ) : null}
              {isInternal && entity?.isOwned ? (
                <Badge variant="outline" size="sm">
                  {tInternalNetwork('ownedPrincipals.title')}
                </Badge>
              ) : null}
            </HStack>

            {headerSubtitle ? (
              <HStack gap="1" alignItems="center" w="full" minW={0}>
                <Link02Icon size={14} aria-hidden style={{ flexShrink: 0 }} />
                <Text
                  fontSize="xs"
                  color="fg.muted"
                  fontFamily="mono"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {headerSubtitle}
                </Text>
              </HStack>
            ) : null}
          </VStack>
        </Box>

        <Box flex="1" overflow="hidden">
          <Tabs.Root
            value={selectedTab}
            size="sm"
            w="full"
            h="full"
            display="flex"
            flexDirection="column"
            overflow="hidden"
            lazyMount
            unmountOnExit
            onValueChange={(details) => setSelectedTab(details.value)}
          >
            <Tabs.List justifyContent="flex-start" px={4}>
              <Tabs.Trigger value="overview" fontSize="sm" px="3" py="2">
                <Text>{tAssetDetail('tabs.details')}</Text>
              </Tabs.Trigger>
              <Tabs.Trigger value="signals" fontSize="sm" px="3" py="2">
                <Text>{tAssetDetail('tabs.signals')}</Text>
              </Tabs.Trigger>
              {isInternal ? (
                <Tabs.Trigger value="relationships" fontSize="sm" px="3" py="2">
                  <Text>{tAssetDetail('tabs.relationships')}</Text>
                </Tabs.Trigger>
              ) : null}
              <Tabs.Indicator />
            </Tabs.List>

            <Tabs.Content value="overview" flex="1" overflow="hidden" pt="0">
              <Scroll orientation="vertical" showScrollbar h="full">
                <Box p={6}>
                  {isInternal && entity && internalEntityKind ? (
                    <VStack gap={8} alignItems="stretch">
                      <DrawerGeneralDetails
                        title={tAssetDetail('generalDetails')}
                        rows={internalOverviewRows}
                      />
                      {entity.properties ? (
                        <Box
                          borderWidth="1px"
                          borderColor="border.subtle"
                          borderRadius="md"
                          overflow="hidden"
                        >
                          <Box
                            px={4}
                            py={3}
                            borderBottomWidth="1px"
                            borderColor="border.subtle"
                          >
                            <Text
                              fontSize="xs"
                              fontWeight="semibold"
                              color="fg.muted"
                              textTransform="uppercase"
                              letterSpacing="wide"
                            >
                              Properties
                            </Text>
                          </Box>
                          <Box
                            as="pre"
                            fontSize="xs"
                            p={4}
                            overflow="auto"
                            whiteSpace="pre-wrap"
                            fontFamily="mono"
                            color="fg.muted"
                          >
                            {JSON.stringify(entity.properties, null, 2)}
                          </Box>
                        </Box>
                      ) : null}
                    </VStack>
                  ) : externalAsset && externalEntityKind && surfaceId ? (
                    <VStack gap={8} alignItems="stretch">
                      {buildExternalOverviewContent({
                        entityKind: externalEntityKind,
                        asset: externalAsset,
                        dateFormatter,
                        t: tAssetDetail,
                        tEntityKinds: tExternalEntityKinds,
                      })}
                      <ExternalNetworkAssetRouteGraph
                        surfaceId={surfaceId}
                        entityId={externalAsset.id}
                        entityKind={externalEntityKind}
                      />
                    </VStack>
                  ) : (
                    <Skeleton h="240px" w="full" borderRadius="md" />
                  )}
                </Box>
              </Scroll>
            </Tabs.Content>

            <Tabs.Content value="signals" flex="1" overflow="hidden" pt="0">
              <Box h="full" w="full" overflow="hidden">
                <SignalsContent
                  surfaceAssetEntityId={entityId}
                  surfaceAssetKind={surfaceAssetKind}
                />
              </Box>
            </Tabs.Content>

            {isInternal ? (
              <Tabs.Content
                value="relationships"
                flex="1"
                overflow="hidden"
                pt="0"
              >
                {internalRelationships.isLoading ? (
                  <Box p={6}>
                    <Skeleton h="240px" w="full" borderRadius="md" />
                  </Box>
                ) : (
                  <RelationshipsSection
                    relationships={internalRelationships.relationships}
                  />
                )}
              </Tabs.Content>
            ) : null}
          </Tabs.Root>
        </Box>
      </VStack>
    </Box>
  );
}
